/**
 * How the position calculation works
 *
 * Terms
 * - Viewport: The visible part of the frame or window in which the overlay will be displayed.
 * - Target: The HTML item which is the logical parent of the displayed content. For example the button, which is
 *   clicked on to open the displayed content.
 * - Tip: A small triangle which is a visual connection between the target and the displayed content.
 * - Overlay: The popup-like container of the displayed content which could be connected to the target using the tip.
 *
 * Calculation
 * The calculation is about finding the optimal position of the overlay. The optimal position is where the most
 * interactive elements are available for the user without extra effort (e.g. scrolling, swipe, or others). Main rule,
 * that all elements of the overlay should be available for the user somehow. This means, that the overlay cannot be
 * placed so that its elements are unreachable.
 *
 * The final result of the calculation is an offset and a position string instead of a coordinate pair, because the
 * overlay itself is displayed by the qtip2 library.
 *
 * The calculation is done in more steps:
 * Step 1: Calculate and collect sizes
 *         For an easier calculation we should collect all necessary sizes. The getting solution could be different in
 *         different browsers. To calculate the placement, we need the position and the size of
 *         - the target's viewport (size, scroll),
 *         - the overlay's viewport (size, scroll) if they differs (NOT IMPLEMENTED YET),
 *         - the target (position relative to the viewport, size),
 *         - the overlay (size)
 * Step 2: Select the proper displaying side
 *         It depends on how large space is at the different sides of the target. The order and the availability of the
 *         sides are influenced by a preference list, which could be right, bottom, top and left. The special auto value
 *         is the short name of a predefined ordered list of the values above.
 * Step 3: Calculate the offset
 *         Based on the selected side and the calculated sizes we give one number: the offset of the tip on the proper
 *         side of the overlay. This is calculated in a way, where the hidden areas and the relevant scroll position
 *         does matter to reach a better, user-friendlier behavior.
 * Step 4: Library configuration
 *         Using the calculated values will be the qtip2 configured to set the proper position of the overlay. It uses
 *         the offset of the tip and the selected side details, which are predefined in a JS object.
 *
 * Notes:  - Within the calculations the center of the target and the middle line of the tip is used.
 *         - All of this calculations will be moved in a separated widget without the underlying qtip2 library, the
 *           current codename of that is Overlay.
 */

import $ from 'jquery';
import * as common from '../../../shared/modules/common';
import { getVisibleClientRect } from '../../../shared/modules/frames';

var DAYSELECTOR_MAX_HEIGHT = 396,
    DAYSELECTOR_MIN_HEIGHT = 260,
    OVERLAY_MAX_HEIGHT = 540,
    OVERLAY_MIN_HEIGHT = OVERLAY_MAX_HEIGHT - (DAYSELECTOR_MAX_HEIGHT - DAYSELECTOR_MIN_HEIGHT),
    OVERLAY_CONTENT_WITHOUT_DAYSELECTOR_HEIGHT = OVERLAY_MAX_HEIGHT - DAYSELECTOR_MAX_HEIGHT,
    OVERLAY_WIDTH = 440,
    OVERLAY_PADDING = 10,
    TIP_SIZE = 15,
    TIP_MIN_ADJUST = OVERLAY_PADDING + TIP_SIZE / 2, // 27.5
    TIP_CENTER_OFFSET_X = 1;

/**
 * A position is on horizont, if it is parallel with the horizontal line of the target.
 */
function isOpeningVertically(position) {
    return position === 'top' || position === 'bottom';
}

function getPositionsOnTheOtherAxis(position) {
    return (isOpeningVertically(position)) ? ['left', 'right'] : ['top', 'bottom'];
}

/**
 * @param {object} distances Parameter object, which contains the values below.
 *                 - fullOverlaySize The full size of the overlay.
 *                 - visibleDistanceBeforeTipTarget The distance between the target and the window border (near to
 *                   the origo of the used coordinate system).
 *                 - visibleDistanceAfterTipTarget The distance between the target and the window border (far from
 *                   the origo of the used coordinate system).
 *                 - pageScrollBeforeViewport The scrollable part of the page before (top or left from) the
 *                   viewport.
 * @return The offset for the qtip2 triangle.
 */
export function calculateOffset(distances) {
    var offset = 0,
        fullOverlaySize = distances.fullOverlaySize,
        visibleDistanceBeforeTipTarget = distances.visibleDistanceBeforeTipTarget,
        visibleDistanceAfterTipTarget = distances.visibleDistanceAfterTipTarget,
        pageScrollBeforeViewport = distances.pageScrollBeforeViewport,
        halfOverlaySize = fullOverlaySize / 2,
        invisibleOverlaySize = Math.max(0, fullOverlaySize
            - (visibleDistanceBeforeTipTarget + visibleDistanceAfterTipTarget));

    function calculateOffsetBySize(size, useScrollInHiddenPlacing) {
        var moveToPageOffset = useScrollInHiddenPlacing
            ? Math.max(0, Math.min(invisibleOverlaySize / 2, pageScrollBeforeViewport))
            : invisibleOverlaySize;
        return Math.min(halfOverlaySize - size, halfOverlaySize) - moveToPageOffset;
    }

    if (visibleDistanceBeforeTipTarget < halfOverlaySize) {
        offset = -1 * calculateOffsetBySize(visibleDistanceBeforeTipTarget, true);
    } else if (visibleDistanceAfterTipTarget < halfOverlaySize) {
        offset = calculateOffsetBySize(visibleDistanceAfterTipTarget, false);
    }

    return offset;
}

/**
 * Calculate the input parameters required by the calculateOffset from the selected position and the sizes object.
 * @param {string} selectedPosition The position side where the overlay will be placed.
 * @param {object} sizes The object which contains all relevant size values.
 * @return An object which contains all parameter required by the calculateOffset (fullOverlaySize,
 *         visibleDistanceBeforeTipTarget, visibleDistanceAfterTipTarget, pageScrollBeforeViewport).
 *
 *         For more info please check the documentation of calculateOffset method.
 */
export function calculateOffsetInputs(selectedPosition, sizes) {
    var result = {};
    if ((isOpeningVertically(selectedPosition))) {
        result.fullOverlaySize = sizes.overlay.width;
        result.visibleDistanceBeforeTipTarget =
            Math.max(sizes.item.topLeftX + (sizes.item.width / 2),
                TIP_MIN_ADJUST + TIP_CENTER_OFFSET_X);
        result.visibleDistanceAfterTipTarget =
            Math.max(sizes.viewport.width - (sizes.item.bottomRightX + (sizes.item.width / 2)),
                TIP_MIN_ADJUST - TIP_CENTER_OFFSET_X);
        result.pageScrollBeforeViewport = sizes.viewport.scrollX;
    } else {
        result.fullOverlaySize = sizes.overlay.height;
        result.visibleDistanceBeforeTipTarget =
            Math.max(sizes.item.topLeftY + (sizes.item.height / 2),
                TIP_MIN_ADJUST);
        result.visibleDistanceAfterTipTarget =
            Math.max(sizes.viewport.height - (sizes.item.bottomRightY + (sizes.item.height / 2)),
                TIP_MIN_ADJUST);
        result.pageScrollBeforeViewport = sizes.viewport.scrollY;
    }
    return result;
}

function calculateOverlayHeight(viewportVisibleClientRect) {
    return viewportVisibleClientRect.height >= OVERLAY_MAX_HEIGHT
        ? OVERLAY_MAX_HEIGHT
        : Math.max(viewportVisibleClientRect.height, OVERLAY_MIN_HEIGHT);
}


/**
 * Generate the required sizes into a parameter object from the bounding rectangles.
 * @param {object} targetBoundingClientRect Target dimension info, DOMRect object + scrollLeft, scrollTop.
 * @param {object} viewportVisibleClientRect Viewport dimension info, extended DOMRect (provided by the
 *                 frames/getVisibleClientRect method) object.
 * @return {object} A parameter object, which contains information about
 *                  - the viewport (width, height),
 *                  - the item (target) (topLeftX, topLeftY, bottomRightX, bottomRightY, width, height),
 *                  - the overlay (width, height, function getSize),
 *                  - function isSmall() to tell that the overlay can be placed in the viewport
 */
export function generateSizes(targetBoundingClientRect, viewportVisibleClientRect) {
    var topLeftX = targetBoundingClientRect.left
        + Math.min(0, viewportVisibleClientRect.absLeft, viewportVisibleClientRect.left),
        topLeftY = targetBoundingClientRect.top
            + Math.min(0, viewportVisibleClientRect.absTop, viewportVisibleClientRect.top),
        targetWidth = targetBoundingClientRect.width,
        targetHeight = targetBoundingClientRect.height;

    return {
        viewport: {
            width: viewportVisibleClientRect.width,
            height: viewportVisibleClientRect.height,
            scrollX: viewportVisibleClientRect.scrollLeft,
            scrollY: viewportVisibleClientRect.scrollTop
        },
        item: {
            'topLeftX': topLeftX,
            'topLeftY': topLeftY,
            'bottomRightX': topLeftX + targetWidth,
            'bottomRightY': topLeftY + targetHeight,
            'width': targetWidth,
            'height': targetHeight
        },
        overlay: {
            width: OVERLAY_WIDTH,
            height: calculateOverlayHeight(viewportVisibleClientRect),
            getSize: function (position) {
                var size = {};
                if (position === 'right' || position === 'left') {
                    size.major = this.width;
                    size.minor = this.height;
                } else if (isOpeningVertically(position)) {
                    size.major = this.height;
                    size.minor = this.width;
                } else {
                    size = {
                        major: Math.max(this.width, this.height),
                        minor: Math.min(this.width, this.height)
                    };
                }
                return size;
            }
        },
        isSmall: function () {
            return this.viewport.width < this.overlay.width || this.viewport.height < this.overlay.height;
        }
    };
}

/**
 * @param $target The jQuery DOM object, which is the target of the overlay.
 * @return A sizes object, which contains the viewport, item and overlay geometries, and an isSmall() method,
 *         which tells that the overlay can be placed in the viewport or the viewport is too small.
 */
function calculateSizes($target) {
    var viewportVisibleClientRect = getVisibleClientRect(window),
        targetBoundingClientRect = $.extend({}, common.getBoundingClientRect($target.get(0))),
        sizes;

    if (!targetBoundingClientRect.width) {
        targetBoundingClientRect.width = $target.outerWidth();
    }
    if (!targetBoundingClientRect.height) {
        targetBoundingClientRect.height = $target.outerHeight();
    }

    viewportVisibleClientRect.scrollTop = $(window).scrollTop();
    viewportVisibleClientRect.scrollLeft = $(window).scrollLeft();

    sizes = generateSizes(
        targetBoundingClientRect,
        viewportVisibleClientRect
    );
    return sizes;
}

/**
 * @param requestedPosition An array of names for the position, where the overlay should be placed.
 * @param target The jQuery DOM object, which is the target of the overlay.
 * @return A string which contains the name of the best position, where the overlay can be positioned.
 */
function findBestPosition(requestedPosition, target, sizes) {
    var positionCalcValues = {
        top: sizes.item.topLeftY,
        right: sizes.viewport.width - sizes.item.bottomRightX,
        bottom: sizes.viewport.height - sizes.item.bottomRightY,
        left: sizes.item.topLeftX
    },
        i,
        tempDiff = {},
        foundPosition,
        bestPosition = { major: 0, minor: 0, position: undefined },
        tempPos,
        minorPositions;
    if (sizes.isSmall()) {
        foundPosition = requestedPosition[0];
    }
    for (i = 0; i < requestedPosition.length; i++) {
        tempPos = requestedPosition[i];
        minorPositions = getPositionsOnTheOtherAxis(tempPos);
        tempDiff.major = positionCalcValues[tempPos] - sizes.overlay.getSize(tempPos).major;
        tempDiff.minor = Math.min(positionCalcValues[minorPositions[0]], positionCalcValues[minorPositions[1]]);
        if (tempDiff.major > bestPosition.major && tempDiff.minor > bestPosition.minor) {
            bestPosition.major = Math.max(bestPosition.major, tempDiff.major);
            bestPosition.minor = Math.max(bestPosition.minor, tempDiff.minor);
            bestPosition.position = tempPos;
        }
    }

    if (!bestPosition.position) {
        foundPosition = requestedPosition[0];
    } else {
        foundPosition = bestPosition.position;
    }

    return foundPosition;
}

/**
 * @param requestedPosition An array of names for the position (order is the precendence), where the overlay
 *        should be placed. The 'auto' value is special, it will be translated to an array of ['right',
 *        'bottom', 'top', 'left']. Every additional items are ignored.
 * @param target The jQuery DOM object, which is the target of the overlay.
 * @return The position details, which is required to configure qtip2 properly.
 */
function calculatePosition(requestedPosition, $target) {
    var positions = {
        'top': { atPos: 'top center', myPos: 'bottom center', adjust: { x: 0, y: 7 } },
        'right': { atPos: 'right center', myPos: 'left center', adjust: { x: -7, y: 0 } },
        'bottom': { atPos: 'bottom center', myPos: 'top center', adjust: { x: 0, y: -7 } },
        'left': { atPos: 'left center', myPos: 'right center', adjust: { x: 7, y: 0 } }
    },
        sizes = calculateSizes($target),
        calculatedPosition,
        offsetCalculationValues;

    if ($.inArray('auto', requestedPosition) > -1) {
        requestedPosition = ['right', 'bottom', 'top', 'left'];
    }
    if (requestedPosition.length > 1) {
        requestedPosition = [findBestPosition(requestedPosition, $target, sizes)];
    }
    calculatedPosition = positions[requestedPosition[0]];
    offsetCalculationValues = calculateOffsetInputs(requestedPosition[0], sizes);
    calculatedPosition.offset = calculateOffset(offsetCalculationValues);
    calculatedPosition.target = $target;
    calculatedPosition.overlayHeight = sizes.overlay.height;
    calculatedPosition.selectorContentHeight = sizes.overlay.height - OVERLAY_CONTENT_WITHOUT_DAYSELECTOR_HEIGHT;
    return calculatedPosition;
}

function isClickedOnScrollbar(event) {
    var $target = common.getEventTarget(event),
        document,
        clientWidth,
        clientHeight;

    if (!$target.get(0)) {
        return false;
    }

    document = $target.get(0).ownerDocument;
    clientWidth = document.documentElement.clientWidth;
    clientHeight = document.documentElement.clientHeight;
    return event.clientX > clientWidth || event.clientY > clientHeight;
}

/**
 * A class which represents a Tooltip component.
 * This defines the structure and the main behavior of the component.
 *
 * @param conf A JSON object, which contains the following parameters:
 *      calendar: a Calendar object,
 *      icon: an Icon object,
 *      openingMode: the opening mode of Date Picker (focus or click),
 *      placement: priority list of names for the position, where the overlay should be placed
 *      $target: the tooltip will be positioned in relation to this element
 */
export default function Tooltip(conf) {
    /* eslint complexity: ["error", 9] */
    function isPositionChanged() {
        var calculatedPosition = calculatePosition(conf.placement, conf.icon.getView()),
            changed = conf.$target.qtip('option', 'position.adjust.x') !== calculatedPosition.adjust.x
                || conf.$target.qtip('option', 'position.adjust.y') !== calculatedPosition.adjust.y
                || conf.$target.qtip('option', 'position.at') !== calculatedPosition.atPos
                || conf.$target.qtip('option', 'position.my') !== calculatedPosition.myPos
                || conf.$target.qtip('option', 'style.height') !== calculatedPosition.overlayHeight
                || conf.$target.qtip('option', 'style.tip.mimic') !== calculatedPosition.myPos
                || conf.$target.qtip('option', 'style.tip.offset') !== calculatedPosition.offset;
        return changed;
    }

    /**
     * Detach the Calendar view elements from the tooltip DOM to keep Calendar view with all data and events.
     * This clean up is necessary before the Qtip re-initialization which will destroy the already added contents.
     *
     * @param $target jQuery element to which the tooltip is opened.
     */
    function detachTooltipContent($target) {
        var qtip = $target.qtip('api'),
            rendered = qtip && qtip.elements && qtip.elements.content;
        if (rendered) {
            qtip.elements.content.children('.uwr-date-picker-calendar').detach();
        }
    }

    /**
     * Check if the open date picker's icon is clicked
     */
    function clickedOnDatePickerIcon($icon) {
        var iconMarkup = $icon.prev(),
            iconTooltipId = iconMarkup.attr('aria-describedby'),
            qtipContent = conf.calendar.getView().parent(),
            qtip = qtipContent.parent(),
            tooltipId = qtip.attr('id');
        return iconTooltipId === tooltipId;
    }

    function hideHandler(event) {
        var ev = common.fixEvent(event),
            originalEvent = ev.originalEvent,
            $target = $(common.getEventTarget(originalEvent)),
            isInsideIcon = $target.hasClass('uwr-date-picker-icon-inside');
        if (isClickedOnScrollbar(originalEvent)) {
            return false;
        } else if (isInsideIcon && clickedOnDatePickerIcon($target)) {
            ev.originalEvent = undefined;
            return false;
        }
    }

    function renderHandler() {
        var $tooltipContent = conf.$target.qtip('api').elements.content;
        detachTooltipContent(conf.$target);
        $tooltipContent.append(conf.calendar.getView());
    }

    function showHandler() {
        if (isPositionChanged()) {
            init();
            conf.$target.qtip('show');
        }
    }

    function visibleHandler() {
        conf.calendar.showDate();
    }

    /**
     * Initialize the qtip on Date Picker target DOM element, or re-initialize qtip with new 'position' and 'style'
     * parameters if the qtip is already opened.
     *
     * @param config changed qtip properties
     */
    function initQtip(config) {
        var qtip = conf.$target.qtip('api'),
            opened = qtip && qtip.elements && qtip.elements.content && !qtip.elements.content.is(':hidden');

        detachTooltipContent(conf.$target);

        // qtip.disable() API function is available only after qtip initialiazion
        if (qtip && qtip.disable) {
            // Qtip re-initialization requires prior disabling to avoid javascript errors in qtip show()
            qtip.disable(true);
        }

        conf.$target.qtip(config);

        if (opened) {
            conf.$target.qtip('show');
        }
    }

    function init() {
        var calculatedPosition = calculatePosition(conf.placement, conf.icon.getView()),
            showEvent = conf.openingMode === 'click' ? 'click' : 'focus',
            config = {
                content: {
                    text: ' '
                },
                events: {
                    hide: hideHandler,
                    render: renderHandler,
                    visible: visibleHandler,
                    show: showHandler
                },
                hide: {
                    delay: 100,
                    event: 'unfocus',
                    fixed: true
                },
                show: {
                    event: showEvent,
                    solo: '.uwr-date-picker-tooltip'
                },
                position: {
                    adjust: {
                        x: calculatedPosition.adjust.x,
                        y: calculatedPosition.adjust.y
                    },
                    at: calculatedPosition.atPos,
                    my: calculatedPosition.myPos,
                    target: calculatedPosition.target
                },
                style: {
                    classes: 'uwr-ui-tooltip-base uwr-ui-tooltip-neutral uwr-date-picker-tooltip',
                    tip: {
                        corner2: true,
                        width: TIP_SIZE,
                        height: TIP_SIZE,
                        mimic: calculatedPosition.myPos,
                        offset: calculatedPosition.offset
                    },
                    width: OVERLAY_WIDTH,
                    height: calculatedPosition.overlayHeight
                }
            };

        if (conf.openingMode === 'focus') {
            conf.icon.getView().on('click tap', function () {
                conf.$target.focus();
            });
        }
        initQtip(config);
        conf.calendar.setSelectorHeight(calculatedPosition.selectorContentHeight);
    }

    init();
}



//////////////////
// WEBPACK FOOTER
// ./widgets/DatePicker/js/datePickerTooltip.js
// module id = 85
// module chunks = main